import {
  ChartEvent,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js'
import { useEffect, useRef, useState } from 'react'
import { Radar } from 'react-chartjs-2'
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter'
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json'

import {
  MentorPsychometrics,
  PsychometricsCategory,
  PsychometricsSubcategories,
} from '../../../../../types/persona'
import { TouchcastPsychomenticsTypes } from '../../PromptView'

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
)

SyntaxHighlighter.registerLanguage('json', json)

const tooltipDescriptions: { [key: string]: string } = {
  // Communication metrics
  Assertiveness:
    'The ability to confidently express opinions and needs while respecting others. High scores indicate effective self-advocacy without being aggressive.',
  Pace: 'The speed and rhythm of speech delivery. Optimal scores show ability to adjust speaking speed to match audience comprehension and engagement.',
  Tone: 'The emotional quality and attitude conveyed through voice. Higher scores indicate appropriate emotional expression and professional demeanor.',
  Clarity:
    'How well ideas are expressed and understood. High scores show effective communication that minimizes misunderstandings.',
  Empathy:
    "The ability to understand and share others' feelings. High scores indicate strong emotional awareness and connection with others.",
  'Self-Awareness':
    "Understanding of one's own emotions and their impact. Higher scores show good emotional regulation and professional behavior.",
  Adaptability:
    'Ability to adjust communication style to different situations. High scores indicate flexibility in various professional contexts.',
  Regulation:
    'Control over emotional expressions and responses. Higher scores show mature handling of workplace situations.',
  'Language Choice':
    'Selection of appropriate vocabulary and expressions. High scores indicate professional and context-appropriate communication.',
  Structure:
    'Organization and flow of communication. Higher scores show logical and well-organized expression of ideas.',
  Etiquette:
    'Adherence to professional communication norms. High scores indicate strong understanding of workplace courtesy.',
  Honorifics:
    'Appropriate use of formal titles and respect markers. Higher scores show proper recognition of professional hierarchy.',
  'Technical Language':
    'Appropriate use of industry-specific terms. High scores indicate strong command of professional vocabulary.',
  'Topic Focus':
    'Ability to maintain relevant discussion points. Higher scores show effective topic management and purposeful communication.',
  'Boundary Maintenance':
    'Respect for professional relationships and limits. High scores indicate appropriate workplace relationship management.',
  'Time Management':
    "Efficient use of communication time. Higher scores show respect for others' time and concise expression.",
  Expertise:
    'Demonstration of subject matter knowledge. High scores indicate strong professional competency and credibility.',

  // Big 5 traits
  Neuroticism:
    'Tendency to experience negative emotions and sensitivity to stress. Lower scores indicate better emotional stability and resilience.',
  Extraversion:
    'Level of sociability and enthusiasm in social situations. Higher scores show greater comfort with social interaction and energy.',
  Openness:
    'Appreciation for new experiences and ideas. Higher scores indicate creativity and intellectual curiosity.',
  Agreeableness:
    'Tendency to be compassionate and cooperative. Higher scores show greater empathy and consideration for others.',
  Conscientiousness:
    'Organization and goal-directed behavior. Higher scores indicate greater reliability and methodical approach to tasks.',
}

const categoryColors = {
  'Verbal Communication': {
    border: 'rgba(255, 99, 132, 1)',
    background: 'rgba(255, 99, 132, 0.5)',
    slider: '#ff6384',
  },
  'Emotional Intelligence': {
    border: 'rgba(54, 162, 235, 1)',
    background: 'rgba(54, 162, 235, 0.5)',
    slider: '#36a2eb',
  },
  Formality: {
    border: 'rgba(255, 206, 86, 1)',
    background: 'rgba(255, 206, 86, 0.5)',
    slider: '#ffce56',
  },
  Professionalism: {
    border: 'rgba(75, 192, 192, 1)',
    background: 'rgba(75, 192, 192, 0.5)',
    slider: '#4bc0c0',
  },
  'Big 5': {
    border: 'rgba(165, 120, 255, 1)',
    background: 'rgba(160, 112, 255, 0.5)',
    slider: '#a578ff',
  },
  'Work Style': {
    border: 'rgba(255, 159, 64, 1)',
    background: 'rgba(255, 159, 64, 0.5)',
    slider: '#ff9f40',
  },
  'Social Dynamics': {
    border: 'rgba(255, 99, 255, 1)',
    background: 'rgba(255, 99, 255, 0.5)',
    slider: '#ff63ff',
  },
  Core: {
    border: 'rgba(64, 224, 208, 1)',
    background: 'rgba(64, 224, 208, 0.5)',
    slider: '#40e0d0',
  },
}

export interface PsychometricAnalysisProps {
  mentorPsychometrics?: MentorPsychometrics
  isLoading?: boolean
  onBig5Update: (data: MentorPsychometrics['big5']) => void
  onConversationDataUpdate: (data: { [key: string]: number }) => void
  onTouchcastPsychometricsUpdate: (
    key: string,
    data: TouchcastPsychomenticsTypes,
  ) => void
  setIsPsychometricUpdateNeeded: (val: boolean) => void
}

function convertCamelToEnglish(key: string): string {
  return (
    key
      // Insert a space before uppercase letters and capitalize the first character
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, match => match.toUpperCase())
      .trim()
  )
}

export function PsychometricAnalysis({
  setIsPsychometricUpdateNeeded,
  mentorPsychometrics,
  isLoading,
  onBig5Update,
  onConversationDataUpdate,
  onTouchcastPsychometricsUpdate,
}: PsychometricAnalysisProps) {
  const [selectedProfile, setSelectedProfile] = useState<MentorPsychometrics>()
  const [modifiedScores, setModifiedScores] = useState<{
    [key: string]: number
  }>({})
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null)
  const [modifiedBig5, setModifiedBig5] = useState<
    MentorPsychometrics['big5'] | undefined
  >(selectedProfile?.big5)
  const [modifiedWorkStyle, setModifiedWorkStyle] = useState<
    MentorPsychometrics['touchcastPsychometrics']['workStyle'] | undefined
  >(selectedProfile?.touchcastPsychometrics.workStyle)
  const [modifiedSocialDynamics, setModifiedSocialDynamics] = useState<
    MentorPsychometrics['touchcastPsychometrics']['socialDynamics'] | undefined
  >(selectedProfile?.touchcastPsychometrics.socialDynamics)
  const [modifiedCore, setModifiedCore] = useState<
    MentorPsychometrics['touchcastPsychometrics']['core'] | undefined
  >(selectedProfile?.touchcastPsychometrics.core)
  const communicationChartRef = useRef<ChartJS<'radar'>>(null)
  const big5ChartRef = useRef<ChartJS<'radar'>>(null)
  const socialCoreChartRef = useRef<ChartJS<'radar'>>(null)

  useEffect(() => {
    onConversationDataUpdate(modifiedScores)
  }, [modifiedScores])

  useEffect(() => {
    if (modifiedBig5) {
      onBig5Update(modifiedBig5)
    }
  }, [modifiedBig5])

  useEffect(() => {
    onTouchcastPsychometricsUpdate('workStyle', modifiedWorkStyle)
  }, [modifiedWorkStyle])

  useEffect(() => {
    onTouchcastPsychometricsUpdate('socialDynamics', modifiedSocialDynamics)
  }, [modifiedSocialDynamics])

  useEffect(() => {
    onTouchcastPsychometricsUpdate('core', modifiedCore)
  }, [modifiedCore])

  useEffect(() => {
    if (mentorPsychometrics) {
      setSelectedProfile(mentorPsychometrics)
      setModifiedBig5(mentorPsychometrics.big5)
      setModifiedWorkStyle(mentorPsychometrics.touchcastPsychometrics.workStyle)
      setModifiedSocialDynamics(
        mentorPsychometrics.touchcastPsychometrics.socialDynamics,
      )
      setModifiedCore(mentorPsychometrics.touchcastPsychometrics.core)
    }
  }, [mentorPsychometrics])

  const getChartData = () => {
    if (!selectedProfile) return null

    const subcategoryMap: Map<string, PsychometricsSubcategories> = new Map()
    selectedProfile.conversationData.categories.forEach(
      (category: PsychometricsCategory) => {
        category.subcategories.forEach((sub: PsychometricsSubcategories) => {
          if (!subcategoryMap.has(sub.name)) {
            subcategoryMap.set(sub.name, {} as PsychometricsSubcategories)
          }
          subcategoryMap.get(sub.name)[category.categoryTitle] =
            modifiedScores[`${category.categoryTitle}_${sub.name}`] !==
            undefined
              ? modifiedScores[`${category.categoryTitle}_${sub.name}`]
              : sub.score
        })
      },
    )

    const labels = Array.from(subcategoryMap.keys())

    const datasets = selectedProfile.conversationData.categories.map(
      category => ({
        label: category.categoryTitle,
        data: labels.map(label => {
          const categoryData = subcategoryMap.get(label)
          // eslint-disable-next-line  @typescript-eslint/no-unsafe-return
          return categoryData[category.categoryTitle] || 0
        }),
        backgroundColor:
          categoryColors[category.categoryTitle as keyof typeof categoryColors]
            .background,
        borderColor:
          categoryColors[category.categoryTitle as keyof typeof categoryColors]
            .border,
        borderWidth: 2,
        pointBackgroundColor:
          categoryColors[category.categoryTitle as keyof typeof categoryColors]
            .border,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor:
          categoryColors[category.categoryTitle as keyof typeof categoryColors]
            .border,
        fill: true,
      }),
    )

    return {
      labels,
      datasets,
    }
  }

  const getBig5ChartData = () => {
    if (!selectedProfile) return null

    delete selectedProfile?.big5?.__typename
    delete modifiedBig5?.__typename
    delete modifiedWorkStyle?.__typename

    const big5Labels = Object.keys(selectedProfile.big5)
    const workStyleLabels = Object.keys(modifiedWorkStyle ?? {})
    const allLabels = [...big5Labels, ...workStyleLabels]

    return {
      labels: allLabels,
      datasets: [
        {
          label: 'Big 5 Personality Traits',
          data: [
            ...Object.values(modifiedBig5 ?? {}),
            ...(Array(workStyleLabels.length).fill(null) as number[]),
          ],
          backgroundColor: categoryColors['Big 5'].background,
          borderColor: categoryColors['Big 5'].border,
          borderWidth: 2,
          pointBackgroundColor: categoryColors['Big 5'].border,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: categoryColors['Big 5'].border,
          fill: true,
        },
        {
          label: 'Work Style',
          data: [
            ...(Array(big5Labels.length).fill(null) as number[]),
            ...Object.values(modifiedWorkStyle ?? {}),
          ],
          backgroundColor: categoryColors['Work Style'].background,
          borderColor: categoryColors['Work Style'].border,
          borderWidth: 2,
          pointBackgroundColor: categoryColors['Work Style'].border,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: categoryColors['Work Style'].border,
          fill: true,
        },
      ],
    }
  }

  const getSocialCoreChartData = () => {
    if (!selectedProfile) return null

    delete selectedProfile?.touchcastPsychometrics?.socialDynamics?.__typename
    delete selectedProfile?.touchcastPsychometrics?.core?.__typename
    delete modifiedSocialDynamics?.__typename
    delete modifiedCore?.__typename

    const socialDynamicsLabels = Object.keys(
      selectedProfile.touchcastPsychometrics.socialDynamics,
    )
    const coreLabels = Object.keys(selectedProfile.touchcastPsychometrics.core)
    const allLabels = [...socialDynamicsLabels, ...coreLabels]

    return {
      labels: allLabels,
      datasets: [
        {
          label: 'Social Dynamics',
          data: [
            ...Object.values(modifiedSocialDynamics ?? {}),
            ...(Array(coreLabels.length).fill(null) as number[]),
          ],
          backgroundColor: categoryColors['Social Dynamics'].background,
          borderColor: categoryColors['Social Dynamics'].border,
          borderWidth: 2,
          pointBackgroundColor: categoryColors['Social Dynamics'].border,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: categoryColors['Social Dynamics'].border,
          fill: true,
        },
        {
          label: 'Core Metrics',
          data: [
            ...(Array(socialDynamicsLabels.length).fill(null) as number[]),
            ...Object.values(modifiedCore ?? {}),
          ],
          backgroundColor: categoryColors.Core.background,
          borderColor: categoryColors.Core.border,
          borderWidth: 2,
          pointBackgroundColor: categoryColors.Core.border,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: categoryColors.Core.border,
          fill: true,
        },
      ],
    }
  }

  const handleScoreChange = (
    categoryTitle: string,
    subcategoryName: string,
    value: number,
  ) => {
    setIsPsychometricUpdateNeeded(true)
    setModifiedScores(prev => ({
      ...prev,
      [`${categoryTitle}_${subcategoryName}`]: value,
    }))
  }

  const handleBig5Change = (trait: string, value: number) => {
    setIsPsychometricUpdateNeeded(true)
    setModifiedBig5(prev => ({
      ...((prev as MentorPsychometrics['big5']) ?? {}),
      [trait]: value,
    }))
  }

  const handleWorkStyleChange = (metric: string, value: number) => {
    setIsPsychometricUpdateNeeded(true)
    setModifiedWorkStyle(prev => ({
      ...((prev as MentorPsychometrics['touchcastPsychometrics']['workStyle']) ??
        {}),
      [metric]: value,
    }))
  }

  const handleSocialDynamicsChange = (metric: string, value: number) => {
    setIsPsychometricUpdateNeeded(true)
    setModifiedSocialDynamics(prev => ({
      ...((prev as MentorPsychometrics['touchcastPsychometrics']['socialDynamics']) ??
        {}),
      [metric]: value,
    }))
  }

  const handleCoreChange = (metric: string, value: number) => {
    setIsPsychometricUpdateNeeded(true)
    setModifiedCore(prev => ({
      ...((prev as MentorPsychometrics['touchcastPsychometrics']['core']) ??
        {}),
      [metric]: value,
    }))
  }

  const handleChartClick = (chart: ChartJS | null, event: ChartEvent) => {
    if (!chart) return

    const points = chart.getElementsAtEventForMode(
      event as unknown as Event,
      'nearest',
      { intersect: true },
      false,
    )

    if (points.length === 0) return

    const point = points[0]
    const dataset = chart.data.datasets[point.datasetIndex]
    const label = chart.data.labels?.[point.index]

    // Get mouse position relative to chart
    const rect = (
      (event as unknown as MouseEvent).target as HTMLCanvasElement
    ).getBoundingClientRect()
    const x = (event as unknown as MouseEvent).clientX - rect.left
    const y = (event as unknown as MouseEvent).clientY - rect.top

    // Calculate new value based on distance from center
    const centerX = chart.chartArea.width / 2 + chart.chartArea.left
    const centerY = chart.chartArea.height / 2 + chart.chartArea.top
    const maxDistance =
      Math.min(chart.chartArea.width, chart.chartArea.height) / 2

    const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2)
    const newValue = Math.min(
      Math.max(Math.round((distance / maxDistance) * 100), 0),
      100,
    )

    if (chart === communicationChartRef.current && dataset.label && label) {
      handleScoreChange(dataset.label, label as string, newValue)
    } else if (chart === big5ChartRef.current && label) {
      if (dataset.label === 'Big 5 Personality Traits') {
        handleBig5Change(label as string, newValue)
      } else if (dataset.label === 'Work Style') {
        handleWorkStyleChange(label as string, newValue)
      }
    } else if (chart === socialCoreChartRef.current && label) {
      if (dataset.label === 'Social Dynamics') {
        handleSocialDynamicsChange(label as string, newValue)
      } else if (dataset.label === 'Core Metrics') {
        handleCoreChange(label as string, newValue)
      }
    }
  }

  const renderCategorySliders = (categoryTitles: string[]) => (
    <div className="space-y-8">
      {categoryTitles.map(categoryTitle => {
        const category = selectedProfile?.conversationData.categories.find(
          cat => cat.categoryTitle === categoryTitle,
        )
        if (!category) return null

        const color =
          categoryColors[categoryTitle as keyof typeof categoryColors]

        return (
          <div key={categoryTitle} className="space-y-4">
            <h2
              className="text-xl font-semibold"
              style={{ color: color.border }}
            >
              {categoryTitle}
            </h2>
            {category.subcategories.map(sub => (
              <div key={sub.name} className="space-y-2">
                <div className="flex justify-between items-center group">
                  <div className="flex items-center gap-2">
                    <label className="text-sm">{sub.name}</label>
                    <i
                      className="fa-solid fa-circle-info text-gray-400 cursor-help opacity-0 group-hover:opacity-100 transition-opacity"
                      onMouseEnter={() =>
                        setActiveTooltip(`${categoryTitle}_${sub.name}`)
                      }
                      onMouseLeave={() => setActiveTooltip(null)}
                    />
                  </div>
                  <span className="text-sm">
                    {Math.round(
                      modifiedScores[`${categoryTitle}_${sub.name}`] ??
                        sub.score,
                    )}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={
                    modifiedScores[`${category.categoryTitle}_${sub.name}`] ??
                    sub.score
                  }
                  onChange={e =>
                    handleScoreChange(
                      categoryTitle,
                      sub.name,
                      Number(e.target.value),
                    )
                  }
                  className="w-full"
                  style={{ accentColor: color.slider }}
                />
                {activeTooltip === `${categoryTitle}_${sub.name}` &&
                  tooltipDescriptions[sub.name] && (
                    <div className="text-xs text-gray-400 mt-1">
                      {tooltipDescriptions[sub.name]}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )

  const renderMetricSliders = (
    title: string,
    metrics: { [key: string]: number },
    onChange: (key: string, value: number) => void,
  ) => {
    if (!metrics) return null
    const color = categoryColors[title as keyof typeof categoryColors]

    return (
      <div className="space-y-8">
        <h2 className="text-xl font-semibold" style={{ color: color.border }}>
          {title}
        </h2>
        {Object.entries(metrics).map(([key, value]) =>
          key === '__typename' ? null : (
            <div key={key} className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm capitalize">
                  {convertCamelToEnglish(key.replace(/_/g, ' '))}
                </label>
                <span className="text-sm">{Math.round(value)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={value}
                onChange={e => onChange(key, Number(e.target.value))}
                className="w-full"
                style={{ accentColor: color.slider }}
              />
            </div>
          ),
        )}
      </div>
    )
  }

  return (
    <div className="h-full space-y-4 !text-white">
      {/* No data view */}
      {!selectedProfile && !isLoading && (
        <div className="text-white text-center p-8">
          {/* <h1 className="text-2xl font-semibold">No Profile Selected json</h1> */}
          <p className="text-gray-400 mt-4">
            No psychometric analysis data found for this persona.
          </p>
        </div>
      )}

      {selectedProfile && (
        <>
          {/* Communication Metrics */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 tc-mv-question-field p-4 rounded-2xl">
            <div className="xl:col-span-3">
              {renderCategorySliders([
                'Verbal Communication',
                'Emotional Intelligence',
              ])}
            </div>

            <div className="xl:col-span-6">
              <div className="aspect-square">
                <Radar
                  ref={communicationChartRef}
                  data={getChartData() ?? { labels: [], datasets: [] }}
                  options={{
                    onClick: event =>
                      handleChartClick(communicationChartRef.current, event),
                    scales: {
                      r: {
                        beginAtZero: true,
                        max: 100,
                        grid: { color: 'rgba(255, 255, 255, 0.1)' },
                        angleLines: { color: 'rgba(255, 255, 255, 0.1)' },
                        ticks: {
                          color: 'rgba(255, 255, 255, 0.8)',
                          backdropColor: 'rgba(0, 0, 0, 1)',
                        },
                        pointLabels: {
                          color: 'rgba(255, 255, 255, 0.8)',
                          font: { size: 12 },
                        },
                      },
                    },
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          color: 'rgba(255, 255, 255, 0.8)',
                          padding: 20,
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: context =>
                            `${context.dataset.label}: ${context.formattedValue}`,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="xl:col-span-3">
              {renderCategorySliders(['Formality', 'Professionalism'])}
            </div>
          </div>

          {/* Big 5 and Work Style */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 tc-mv-question-field p-4 rounded-2xl">
            <div className="xl:col-span-3">
              {renderMetricSliders(
                'Big 5',
                modifiedBig5 as unknown as Record<string, number>,
                handleBig5Change,
              )}
            </div>

            <div className="xl:col-span-6">
              <div className="aspect-square">
                <Radar
                  ref={big5ChartRef}
                  data={getBig5ChartData() ?? { labels: [], datasets: [] }}
                  options={{
                    onClick: event =>
                      handleChartClick(big5ChartRef.current, event),
                    scales: {
                      r: {
                        beginAtZero: true,
                        max: 100,
                        grid: { color: 'rgba(255, 255, 255, 0.1)' },
                        angleLines: { color: 'rgba(255, 255, 255, 0.1)' },
                        ticks: {
                          color: 'rgba(255, 255, 255, 0.8)',
                          backdropColor: 'rgba(0, 0, 0, 1)',
                        },
                        pointLabels: {
                          callback(labelValue) {
                            return convertCamelToEnglish(labelValue)
                          },
                          color: 'rgba(255, 255, 255, 0.8)',
                          font: { size: 12 },
                        },
                      },
                    },
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          color: 'rgba(255, 255, 255, 0.8)',
                          padding: 20,
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: context =>
                            `${context.dataset.label}: ${context.formattedValue}`,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="xl:col-span-3">
              {renderMetricSliders(
                'Work Style',
                modifiedWorkStyle as unknown as Record<string, number>,
                handleWorkStyleChange,
              )}
            </div>
          </div>

          {/* Social Dynamics and Core */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 tc-mv-question-field p-4 rounded-2xl">
            <div className="xl:col-span-3">
              {renderMetricSliders(
                'Social Dynamics',
                modifiedSocialDynamics as unknown as Record<string, number>,
                handleSocialDynamicsChange,
              )}
            </div>

            <div className="xl:col-span-6">
              <div className="aspect-square">
                <Radar
                  ref={socialCoreChartRef}
                  data={
                    getSocialCoreChartData() ?? { labels: [], datasets: [] }
                  }
                  options={{
                    onClick: event =>
                      handleChartClick(socialCoreChartRef.current, event),
                    scales: {
                      r: {
                        beginAtZero: true,
                        max: 100,
                        grid: { color: 'rgba(255, 255, 255, 0.1)' },
                        angleLines: { color: 'rgba(255, 255, 255, 0.1)' },
                        ticks: {
                          color: 'rgba(255, 255, 255, 0.8)',
                          backdropColor: 'rgba(0, 0, 0, 1)',
                        },
                        pointLabels: {
                          callback(labelValue) {
                            return convertCamelToEnglish(labelValue)
                          },
                          color: 'rgba(255, 255, 255, 0.8)',
                          font: { size: 12 },
                        },
                      },
                    },
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          color: 'rgba(255, 255, 255, 0.8)',
                          padding: 20,
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: context =>
                            `${context.dataset.label}: ${context.formattedValue}`,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="xl:col-span-3">
              {renderMetricSliders(
                'Core',
                modifiedCore as unknown as Record<string, number>,
                handleCoreChange,
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
